/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect } from 'react';
import { Card, Container, Grid, Stack } from '@mui/material';
import { useParams } from 'react-router';
import useTask from '../useTask';
import Form from '../../../components/Form';
import PostalAddressEditor from '../PostalAddressEditor';
import Page from '../../../components/Page';
import TaskDetailEditor from '../TaskDetailEditor';
import Task from '../../../models/Task';
import { TaskStatus } from '../../../enums/TaskStatus';
import useUpdateTask from '../useUpdateTask';
import TaskInfo from '../TaskInfo';
import FileByType from '../FilesByType';
import { FileType } from '../../../enums/FileType';
import Modifications from '../Modifications';
import CommentsSection from '../CommentsSection';
import TaskLoading from '../TaskLoading';
import PricingTable from '../CustomerPricing/PricingTable';
import PriceRejectionDialog from '../../../components/PriceRejectionDialog';
import { UserRole } from '../../../enums/UserRole';
import StatusHandlerButtons from '../../../utils/StatusHandler';
import { useQuery } from 'react-query';
import Customer from '../../../models/Customer';
import TextField from '../../../components/Form/fields/TextField';
import useSeenTask from '../useSeenTask';

const AgentEditor: React.FC = () => {
    const { id: taskId } = useParams();
    const { data: task } = useTask(taskId);
    const { data: customer } = useQuery<Customer>(['customers/', task?.customerId], {
        enabled: !!task?.customerId,
    });
    const { mutate: updateTask } = useUpdateTask(taskId);
    const [openPriceRejection, setOpenPriceRejection] = useState<boolean>(false);
    const { mutate: setSeen } = useSeenTask();

    useEffect(() => {
        setSeen({ id: taskId });
    }, [setSeen, taskId]);

    if (!task) {
        return <TaskLoading />;
    }

    const handleUpdateTask = () => {
        if (
            task.status === TaskStatus.INCORRECT_TRANSLATION ||
      task.status === TaskStatus.PRICE_DECLINED
        ) {
            setOpenPriceRejection(true);
        } else {
            updateTask;
        }
    };

    const handleCloseRejection = () => {
        setOpenPriceRejection(false);
    };

    return (
        <Page title="WeTranslate">
            <Form<Task> validationSchema={undefined} onSubmit={handleUpdateTask} initialValues={task}>
                <Container maxWidth={'lg'}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Stack gap={2}>
                                <Card sx={{ p: 2 }}>
                                    <TaskInfo task={task} isCustomer showCreator />
                                </Card>
                                <FileByType fileType={FileType.TRANSLATED} taskId={taskId} />
                                <FileByType fileType={FileType.ORIGINAL} taskId={taskId} />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack gap={2}>
                                <Card sx={{ p: 2, bgcolor: 'background.neutral' }}>
                                    <TaskDetailEditor
                                        disabled={
                                            task.status === TaskStatus.NEW_TASK ||
                      task.status === TaskStatus.INVALID_LANGUAGE ?
                                                false :
                                                true
                                        }
                                    />
                                </Card>
                                {task.hardCopyRequired && (
                                    <Card>
                                        <PostalAddressEditor />
                                    </Card>
                                )}
                                <PricingTable task={task} />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack gap={2}>
                                <StatusHandlerButtons
                                    userId={task.userId}
                                    task={task!}
                                    role={UserRole.AGENT}
                                    size={'small'}
                                    shouldApprovePrice={customer?.mustApprovePrice ?? false}
                                />

                                <CommentsSection />

                                <Modifications taskId={taskId} />

                                <Card sx={{ p: 2 }}>
                                    <TextField name={'merchantId'} label={'Merchant ID'} />
                                </Card>
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
                <PriceRejectionDialog
                    open={openPriceRejection}
                    onClose={handleCloseRejection}
                    task={task}
                    status={task.status!}
                    onEdit={true}
                />
            </Form>
        </Page>
    );
};

export default AgentEditor;
